/**
 * POS Reports — landing hub.
 *
 * Lists the 7 finance reports as responsive cards. Click → drill-down.
 * Designed mobile-first: single column on phones, auto-fill grid on
 * tablet+. Each card has a subtle category accent for visual scanning.
 */

import React, { useState } from 'react';
import { webNavigate } from '../../services/navigate.service';
import { useResponsive } from '../../hook/useResponsive';
import { useLanguage } from './shared/useLanguage';

interface ReportCard {
  path: string;
  title: string;
  description: string;
  /** Small accent colour for the left rail; ties cards to their domain. */
  accent: string;
}

const CARDS: ReportCard[] = [
  {
    path: 'sales-summary',
    title: 'Sales Summary',
    description:
      'Gross, net, tax, discounts, refunds, service charge, transaction count, average.',
    accent: '#0F7679',
  },
  {
    path: 'tender-summary',
    title: 'Tender Summary',
    description:
      'Total paid per tender (cash, card, discount tokens). Counts unique transactions.',
    accent: '#0F7679',
  },
  {
    path: 'sales-by-product-group',
    title: 'Sales by Product Group',
    description:
      'Net, tax, gross, quantity and discounts rolled up per product group.',
    accent: '#0F7679',
  },
  {
    path: 'sales-by-product',
    title: 'Sales by Product',
    description:
      'Same metrics as Sales by Product Group but at the SKU level, with the group as an attribute.',
    accent: '#0F7679',
  },
  {
    path: 'tax-summary',
    title: 'VAT / Tax Summary',
    description:
      'Net, tax, and gross totalled per tax rate. Uses persisted line values.',
    accent: '#7C3AED',
  },
  {
    path: 'refund-summary',
    title: 'Refund Summary',
    description: 'Refund transactions grouped by the user who processed them.',
    accent: '#DC2626',
  },
  {
    path: 'void-summary',
    title: 'Void Summary',
    description: 'Voided transactions and voided lines, grouped by user.',
    accent: '#DC2626',
  },
  {
    path: 'cashup',
    title: 'Z / Cash-up',
    description:
      'Closed sessions with per-tender expected vs declared and variance.',
    accent: '#D97706',
  },
  {
    path: 'cashup-xz',
    title: 'Cash-up (X / Z)',
    description:
      'A live X or Z on screen — the same report the till prints, exportable as PDF.',
    accent: '#D97706',
  },
  {
    path: 'transaction-detail',
    title: 'Transaction Detail Export',
    description:
      'Per-line export for audit. XLSX only; capped at 31 days per export.',
    accent: '#4B5563',
  },
];

const FinanceReportsHub: React.FC = () => {
  const { t } = useLanguage();
  const { isMobile } = useResponsive();
  const [hovered, setHovered] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const languagePrefix = localStorage.getItem('language') || 'en';

  const open = (path: string) => {
    webNavigate(`/${languagePrefix}/pos-reports/${path}`);
  };

  return (
    <div
      className="h-full"
      style={{
        overflowY: 'auto',
        padding: isMobile ? '4px 0 24px' : '4px 0 32px',
      }}
    >
      <p
        className="font-inter"
        style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 500,
          color: '#222525',
          lineHeight: 1.2,
        }}
      >
        {t('POS Reports', 'POS Reports')}
      </p>
      <p
        className="font-inter"
        style={{
          marginTop: 6,
          fontSize: isMobile ? '13px' : '14px',
          color: '#4A5353',
          lineHeight: 1.5,
          maxWidth: 760,
        }}
      >
        {t(
          'POS Reports subtitle',
          'Finance-grade XLSX and PDF exports for day-end reconciliation, VAT filing, and audit.',
        )}
      </p>

      <div
        className="grid"
        style={{
          marginTop: isMobile ? 20 : 28,
          gap: isMobile ? 12 : 16,
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'repeat(auto-fill, minmax(300px, 1fr))',
        }}
      >
        {CARDS.map((c) => {
          const isActive = hovered === c.path || focused === c.path;
          return (
            <button
              type="button"
              key={c.path}
              onClick={() => open(c.path)}
              onMouseEnter={() => setHovered(c.path)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setFocused(c.path)}
              onBlur={() => setFocused(null)}
              className="font-inter text-left"
              style={{
                position: 'relative',
                padding: isMobile ? '16px' : '20px',
                paddingLeft: isMobile ? '20px' : '24px',
                borderRadius: 12,
                border: `1px solid ${isActive ? c.accent : '#EBF0F0'}`,
                background: '#FFFFFF',
                cursor: 'pointer',
                transition:
                  'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                boxShadow: isActive
                  ? `0 6px 16px ${c.accent}1F`
                  : '0 1px 0 rgba(15, 41, 42, 0.02)',
                transform: isActive ? 'translateY(-1px)' : 'translateY(0)',
                outline: 'none',
                width: '100%',
                minHeight: isMobile ? 'auto' : 140,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Left accent rail */}
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 12,
                  bottom: 12,
                  width: 3,
                  borderRadius: 2,
                  background: c.accent,
                  opacity: isActive ? 1 : 0.6,
                  transition: 'opacity 160ms ease',
                }}
              />
              <p
                style={{
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: 600,
                  color: '#05292A',
                  marginBottom: 6,
                  lineHeight: 1.3,
                }}
              >
                {t(c.title, c.title)}
              </p>
              <p
                style={{
                  fontSize: '13px',
                  color: '#4A5353',
                  lineHeight: 1.5,
                  flexGrow: 1,
                }}
              >
                {t(`${c.title} description`, c.description)}
              </p>
              <p
                style={{
                  marginTop: 14,
                  fontSize: '13px',
                  color: c.accent,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {t('Open report', 'Open report')}
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block',
                    transform: isActive ? 'translateX(3px)' : 'translateX(0)',
                    transition: 'transform 160ms ease',
                  }}
                >
                  →
                </span>
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FinanceReportsHub;
